import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  getEventCheckoutSessionDetailsAction,
  syncValidationChanges,
} from "@/modules/eventRegistration/features/EventRegistration/slices/eventCheckoutSlice";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import ShowIfHasAccess from "@/components/ShowIfHasAccess/ShowIfHasAccess";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, AlertCircle, Users, Clock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import EventDetailsHeader from "@/modules/eventRegistration/features/EventRegistration/components/EventDetailsHeader/EventDetailsHeader";
import VoucherRedemption from "@/modules/eventRegistration/features/EventRegistration/components/VoucherRedemption/VoucherRedemption";
import DiscountCodeRedemption from "@/modules/eventRegistration/features/EventRegistration/components/DiscountCodeRedemption/DiscountCodeRedemption";
import AdminDiscountForm from "@/modules/eventRegistration/features/EventRegistration/components/AdminDiscountForm/AdminDiscountForm";
import OrderSummary from "@/modules/eventRegistration/features/EventRegistration/components/OrderSummary/OrderSummary";
import CheckoutLoadingSkeleton from "@/modules/eventRegistration/features/EventRegistration/components/CheckoutLoadingSkeleton/CheckoutLoadingSkeleton";
import WaitlistModal from "@/modules/eventRegistration/features/EventRegistration/components/WaitlistModal/WaitlistModal";
import { useEventCheckoutReservation } from "@/modules/eventRegistration/features/EventRegistration/hooks/useEventCheckoutReservation";
import { processPayment } from "@/modules/eventRegistration/features/EventRegistration/api/processPayment/processPaymentApi";
import { validateCheckoutAvailability } from "@/modules/eventRegistration/features/EventRegistration/api/validateCheckoutAvailability/validateCheckoutAvailabilityApi";
import { ValidateCheckoutAvailabilityResponseData } from "@/modules/eventRegistration/features/EventRegistration/api/validateCheckoutAvailability/types";
import ZeroCostPaymentPanel from "@/modules/eventRegistration/features/EventRegistration/components/ZeroCostPaymentPanel/ZeroCostPaymentPanel";
import PaymentForm from "@/modules/eventRegistration/features/EventRegistration/components/PaymentForm/PaymentForm";
import { calculateCheckoutPrice } from "@/modules/eventRegistration/features/EventRegistration/utils/checkoutPriceCalculator";
import type { EventCheckoutSessionAttendee } from "@/modules/eventRegistration/features/EventRegistration/api/getEventCheckoutSessionDetails/types";

function Checkout() {
  const { eventCheckoutSessionUuid } = useParams<{
    eventCheckoutSessionUuid: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [isVoucherApplied, setIsVoucherApplied] = useState(false);
  const [appliedVoucherQuantity, setAppliedVoucherQuantity] =
    useState<number>(0);
  const [isDiscountApplied, setIsDiscountApplied] = useState(false);
  const [appliedDiscountAmount, setAppliedDiscountAmount] = useState<number>(0);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string | null>(
    null,
  );
  const [isAdminDiscountApplied, setIsAdminDiscountApplied] = useState(false);
  const [adminDiscountAmount, setAdminDiscountAmount] = useState<number>(0);
  const [adminDiscountType, setAdminDiscountType] = useState<
    "percentage" | "fixed_amount" | null
  >(null);
  const [adminDiscountValue, setAdminDiscountValue] = useState<number>(0);
  const [adminDiscountReason, setAdminDiscountReason] = useState<string>("");
  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const [availabilityChanges, setAvailabilityChanges] = useState<string[]>([]);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null,
  );
  const [validationData, setValidationData] =
    useState<ValidateCheckoutAvailabilityResponseData | null>(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistAcknowledged, setWaitlistAcknowledged] = useState(false);

  const { loadingGetDetails, getDetailsError, eventCheckoutSessionDetails } =
    useSelector((state: RootState) => state.eventCheckout);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (eventCheckoutSessionUuid) {
      dispatch(getEventCheckoutSessionDetailsAction(eventCheckoutSessionUuid));
    }
  }, [dispatch, eventCheckoutSessionUuid]);

  // Pass the current reservation expiration to the hook
  const currentReservationExpiresAt =
    eventCheckoutSessionDetails?.reservationExpiresAt ?? null;

  const { timeLeft, formatTimeLeft } = useEventCheckoutReservation({
    existingCheckoutSessionUuid: eventCheckoutSessionUuid,
    reservationExpiresAt: currentReservationExpiresAt,
    eventSessionUuid: eventCheckoutSessionDetails?.eventSessionUuid ?? null,
    getDetailsData: eventCheckoutSessionDetails,
  });

  const attendees = eventCheckoutSessionDetails?.attendees || [];
  const eventName = eventCheckoutSessionDetails?.eventName ?? "Untitled Event";
  const eventPrice = eventCheckoutSessionDetails?.eventPrice ?? 0;
  const availableSeats = eventCheckoutSessionDetails?.availableSeats ?? 0;
  const availableVoucherSeats =
    eventCheckoutSessionDetails?.companyAvailableVoucherSeats || 0;
  const userHeldSeats = eventCheckoutSessionDetails?.userHeldSeats ?? 0;
  const nonWaitlistedAttendeesInCheckout =
    eventCheckoutSessionDetails?.nonWaitlistedAttendeesCount ?? 0;
  const totalCapacity = eventCheckoutSessionDetails?.totalCapacity ?? 0;

  // Calculate current pricing
  const currentPricing = calculateCheckoutPrice(
    attendees,
    eventPrice,
    appliedVoucherQuantity,
    appliedDiscountAmount,
    adminDiscountAmount,
  );

  function handlePaymentSuccess() {
    setIsProcessing(false);
    setPaymentProcessed(true);
    if (eventCheckoutSessionUuid) {
      navigate(
        `/event-registration/events/register/${eventCheckoutSessionUuid}/confirmation`,
      );
    }
  }

  async function handleCompleteRegistration() {
    if (!eventCheckoutSessionUuid) return;
    setIsProcessing(true);

    // First validate availability for zero-cost registrations
    try {
      const validationResult = await validateCheckoutAvailability(
        eventCheckoutSessionUuid,
      );

      if (validationResult.data.hasChanges) {
        setValidationData(validationResult.data);
        setAvailabilityChanges(validationResult.data.changes);
        setAvailabilityMessage(validationResult.data.message);

        // Sync the validation changes (attendee statuses and reservation)
        dispatch(syncValidationChanges(validationResult.data));

        setShowChangesDialog(true);
        setIsProcessing(false);
        return;
      }

      // If no changes, proceed with registration
      await completeZeroCostRegistration();
    } catch {
      setIsProcessing(false);
    }
  }

  async function completeZeroCostRegistration() {
    if (!eventCheckoutSessionUuid) return;

    try {
      const invoiceNumber = `INV${Date.now()}`;
      await processPayment({
        dataDescriptor: "FREE",
        dataValue: "FREE",
        amount: 0,
        shouldCreatePaymentProfile: false,
        invoiceNumber,
        voucherQuantity: appliedVoucherQuantity,
        discountCode: appliedDiscountCode || undefined,
        discountAmount: appliedDiscountAmount,
        adminDiscountType: adminDiscountType || undefined,
        adminDiscountValue,
        adminDiscountReason,
        eventCheckoutSessionUuid,
      });
      setIsProcessing(false);
      setPaymentProcessed(true);
      navigate(
        `/event-registration/events/register/${eventCheckoutSessionUuid}/confirmation`,
      );
    } catch {
      setIsProcessing(false);
    }
  }

  function handleBackToRegistration() {
    navigate(-1);
  }

  const handleCancelAfterChanges = () => {
    setShowChangesDialog(false);
    setValidationData(null);
  };

  const handleManageWaitlist = () => {
    setShowChangesDialog(false);
    setShowWaitlistModal(true);
  };

  const handleAvailabilityChanged = (
    validationData: ValidateCheckoutAvailabilityResponseData,
  ) => {
    dispatch(syncValidationChanges(validationData));
  };

  if (loadingGetDetails) {
    return <CheckoutLoadingSkeleton />;
  }

  const hasWaitlistAttendees = currentPricing.waitlistedAttendeeCount > 0;
  const shouldShowPaymentForm =
    !paymentProcessed && (currentPricing.total > 0 || hasWaitlistAttendees);
  const shouldShowZeroCost =
    !paymentProcessed && currentPricing.total === 0 && !hasWaitlistAttendees;

  // Calculate pricing after validation changes
  const pricingAfterChanges = validationData
    ? calculateCheckoutPrice(
        validationData.attendees
          .map((va) => {
            const original = attendees.find((a) => a.id === va.id);
            return original ? { ...original, isWaitlist: va.isWaitlist } : null;
          })
          .filter(Boolean) as EventCheckoutSessionAttendee[],
        eventPrice,
        appliedVoucherQuantity,
        appliedDiscountAmount,
        adminDiscountAmount,
      )
    : null;

  const hasWaitlistChanges = validationData
    ? validationData.newWaitlistCount > validationData.previousWaitlistCount
    : false;

  return (
    <>
      <MainPageWrapper hideHeader title="Checkout">
        <div className="mb-6">
          <Button
            className="px-0 flex items-center gap-2 text-muted-foreground hover:text-primary"
            onClick={handleBackToRegistration}
            variant="link"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Registration
          </Button>
        </div>
        {getDetailsError && (
          <Alert className="mb-4" variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{getDetailsError}</AlertDescription>
          </Alert>
        )}
        {eventCheckoutSessionDetails && (
          <EventDetailsHeader
            eventData={{ title: eventName, accepts_vouchers: true }}
            sessionData={{
              title: eventCheckoutSessionDetails.eventSessionName,
              start_time: eventCheckoutSessionDetails.startDate,
              end_time: eventCheckoutSessionDetails.endDate,
              timezoneIdentifier:
                eventCheckoutSessionDetails.timezoneIdentifier,
              timezoneShortName: eventCheckoutSessionDetails.timezoneShortName,
            }}
          />
        )}

        {/* Waitlist Alert Banner */}
        {hasWaitlistAttendees && (
          <Alert className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
            <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">
              {currentPricing.waitlistedAttendeeCount ===
              attendees.filter((a) => a.isSelected).length
                ? "All attendees will be waitlisted"
                : "Some attendees will be waitlisted"}
            </AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300">
              <p className="mb-2">
                {currentPricing.waitlistedAttendeeCount} of{" "}
                {attendees.filter((a) => a.isSelected).length} attendee
                {attendees.filter((a) => a.isSelected).length !== 1 ? "s" : ""}{" "}
                will be placed on the waitlist due to limited availability.{" "}
                <button
                  className="underline hover:no-underline font-medium"
                  onClick={() => setShowWaitlistModal(true)}
                  type="button"
                >
                  View details
                </button>
              </p>
              <p className="text-sm">
                Your card will be stored on file. If a seat becomes available,
                we&apos;ll automatically register waitlisted attendees and
                charge your card.
              </p>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <OrderSummary
              adminDiscountAmount={adminDiscountAmount}
              allAttendees={attendees}
              availableSeats={availableSeats}
              checkoutSessionUuid={eventCheckoutSessionUuid || ""}
              codeDiscountAmount={appliedDiscountAmount}
              eventName={eventName}
              eventPrice={eventPrice}
              formatTimeLeft={formatTimeLeft}
              nonWaitlistedAttendeesInCheckout={
                nonWaitlistedAttendeesInCheckout
              }
              paidAttendeeCount={currentPricing.paidAttendeeCount}
              reservationExpiresAt={currentReservationExpiresAt}
              timeLeft={timeLeft}
              total={currentPricing.total}
              totalCapacity={totalCapacity}
              userHeldSeats={userHeldSeats}
              voucherSeatsUsed={appliedVoucherQuantity}
              waitlistedAttendeeCount={currentPricing.waitlistedAttendeeCount}
            />
          </div>
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {availableVoucherSeats > 0 && (
                <VoucherRedemption
                  attendeeCount={
                    currentPricing.paidAttendeeCount +
                    currentPricing.waitlistedAttendeeCount
                  }
                  availableVoucherSeats={availableVoucherSeats}
                  isVoucherApplied={isVoucherApplied}
                  onVoucherApplied={(quantity: number) => {
                    setIsVoucherApplied(true);
                    setAppliedVoucherQuantity(quantity);
                  }}
                  onVoucherRemoved={() => {
                    setIsVoucherApplied(false);
                    setAppliedVoucherQuantity(0);
                  }}
                />
              )}
              {eventCheckoutSessionDetails?.discounts &&
                eventCheckoutSessionDetails.discounts.length > 0 && (
                  <DiscountCodeRedemption
                    allAvailableDiscounts={
                      eventCheckoutSessionDetails.discounts
                    }
                    baseAmount={currentPricing.baseAmount}
                    isDiscountApplied={isDiscountApplied}
                    onDiscountApplied={(discountAmount, discountData) => {
                      setIsDiscountApplied(true);
                      setAppliedDiscountAmount(discountAmount);
                      setAppliedDiscountCode(discountData.code);
                    }}
                    onDiscountRemoved={() => {
                      setIsDiscountApplied(false);
                      setAppliedDiscountAmount(0);
                      setAppliedDiscountCode(null);
                    }}
                  />
                )}
              <ShowIfHasAccess requiredPermissions={["administration-events"]}>
                <AdminDiscountForm
                  baseAmount={currentPricing.baseAmount}
                  isDiscountApplied={isAdminDiscountApplied}
                  onDiscountApplied={(newDiscountAmount, discountData) => {
                    setIsAdminDiscountApplied(true);
                    setAdminDiscountAmount(newDiscountAmount);
                    setAdminDiscountType(discountData.discount_type || null);
                    setAdminDiscountValue(discountData.discount_value || 0);
                    setAdminDiscountReason(discountData.reason || "");
                  }}
                  onDiscountRemoved={() => {
                    setIsAdminDiscountApplied(false);
                    setAdminDiscountAmount(0);
                    setAdminDiscountType(null);
                    setAdminDiscountValue(0);
                    setAdminDiscountReason("");
                  }}
                />
              </ShowIfHasAccess>
              {/* Waitlist Acknowledgment Checkbox */}
              {hasWaitlistAttendees && (
                <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <Checkbox
                    checked={waitlistAcknowledged}
                    id="waitlist-acknowledgment"
                    onCheckedChange={(checked) =>
                      setWaitlistAcknowledged(checked === true)
                    }
                  />
                  <label
                    className="text-sm cursor-pointer"
                    htmlFor="waitlist-acknowledgment"
                  >
                    I understand that{" "}
                    {currentPricing.waitlistedAttendeeCount} attendee
                    {currentPricing.waitlistedAttendeeCount !== 1
                      ? "s"
                      : ""}{" "}
                    will be placed on the waitlist. My card will be stored on
                    file and automatically charged if a seat becomes available.
                  </label>
                </div>
              )}

              {shouldShowPaymentForm && (
                <PaymentForm
                  adminDiscountReason={adminDiscountReason}
                  adminDiscountType={adminDiscountType}
                  adminDiscountValue={adminDiscountValue}
                  discountAmount={appliedDiscountAmount}
                  discountCode={appliedDiscountCode}
                  disabled={hasWaitlistAttendees && !waitlistAcknowledged}
                  eventCheckoutSessionUuid={eventCheckoutSessionUuid}
                  eventPrice={eventPrice}
                  handlePaymentSuccess={handlePaymentSuccess}
                  isProcessing={isProcessing}
                  onAvailabilityChanged={handleAvailabilityChanged}
                  setIsProcessing={setIsProcessing}
                  total={currentPricing.total}
                  voucherQuantity={appliedVoucherQuantity}
                />
              )}
              {shouldShowZeroCost && (
                <ZeroCostPaymentPanel
                  isAdminDiscountApplied={isAdminDiscountApplied}
                  isDiscountApplied={isDiscountApplied}
                  isProcessing={isProcessing}
                  isVoucherApplied={isVoucherApplied}
                  onCompleteRegistration={handleCompleteRegistration}
                />
              )}
              {paymentProcessed && <div />}
            </div>
          </div>
        </div>
      </MainPageWrapper>

      <Dialog onOpenChange={setShowChangesDialog} open={showChangesDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Seat Availability Has Changed
            </DialogTitle>
            <DialogDescription className="pt-4">
              {availabilityMessage && (
                <Alert className="mb-4">
                  <AlertDescription>{availabilityMessage}</AlertDescription>
                </Alert>
              )}
              {validationData && pricingAfterChanges && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Attendee Changes
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Previously</p>
                        <p className="font-medium">
                          {validationData.previousSeatedCount} paid seats
                        </p>
                        {validationData.previousWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {validationData.previousWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-muted-foreground">Now</p>
                        <p className="font-medium">
                          {validationData.newSeatedCount} paid seats
                        </p>
                        {validationData.newWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {validationData.newWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {pricingAfterChanges.total !== currentPricing.total && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                      <h4 className="font-medium">Price Update</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Previous total:
                          </span>
                          <span className="line-through">
                            ${currentPricing.total.toFixed(2)}
                          </span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-medium">
                          <span>New total:</span>
                          <span className="text-lg">
                            ${pricingAfterChanges.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {availabilityChanges.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-sm">What happened:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        {availabilityChanges.map((change, index) => (
                          <li key={index}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={handleCancelAfterChanges} variant="outline">
              Cancel
            </Button>
            {hasWaitlistChanges && (
              <Button onClick={handleManageWaitlist} variant="secondary">
                Manage Waitlist
              </Button>
            )}
            <Button
              onClick={() => {
                setShowChangesDialog(false);
                setIsProcessing(true);
                completeZeroCostRegistration();
              }}
            >
              Continue (Free)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WaitlistModal
        allAttendees={attendees}
        checkoutSessionUuid={eventCheckoutSessionUuid || ""}
        eventName={eventName}
        isOpen={showWaitlistModal}
        nonWaitlistedAttendeesInCheckout={nonWaitlistedAttendeesInCheckout}
        onClose={() => setShowWaitlistModal(false)}
      />
    </>
  );
}

export default Checkout;
